import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';

// Types
interface FloatingPageCardProps {
  isMenuOpen: boolean;
  children: React.ReactNode;
}

// Constants
const MOBILE_BREAKPOINT = 1024;
const CARD_SCALE = 0.6;
const CARD_OFFSET_VW = 6;
const DEBOUNCE_DELAY = 100;

// Optimized hook with debouncing and SSR safety
function useIsMobileOrTablet() {
  const [isMobile, setIsMobile] = useState(() => {
    // SSR-safe initial state
    if (typeof window === 'undefined') return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
  });

  const debouncedResize = useCallback(() => {
    const timeoutId = setTimeout(() => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    }, DEBOUNCE_DELAY);
    
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const cleanup = debouncedResize();
    window.addEventListener('resize', debouncedResize);
    
    return () => {
      cleanup();
      window.removeEventListener('resize', debouncedResize);
    };
  }, [debouncedResize]);

  return isMobile;
}

// Memoized variants to prevent recreation
const variants = {
  closed: {
    scale: 1,
    x: 0,
    y: 0,
    borderRadius: '0rem',
    boxShadow: 'none'
  },
  open: {
    scale: CARD_SCALE,
    x: `calc(${CARD_OFFSET_VW}vw + ${(1 - CARD_SCALE) * 100}vw)`,
    y: 0,
    borderRadius: '2rem',
    // Keep shadows minimal to avoid heavy repaints during animation
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
  }
} as const;

const FloatingPageCard: React.FC<FloatingPageCardProps> = ({ isMenuOpen, children }) => {
  const isMobileOrTablet = useIsMobileOrTablet();

  // Memoize transition config to prevent recreation
  const transition = useMemo(() => ({
    type: 'spring' as const,
    stiffness: isMenuOpen ? 180 : 70,
    damping: isMenuOpen ? 26 : 18,
    mass: 0.9
  }), [isMenuOpen]);

  // Memoize styles to prevent recreation
  const motionStyles = useMemo(() => ({
    overflowY: 'auto' as const,
    position: isMenuOpen ? 'fixed' as const : 'relative' as const,
    top: 0,
    left: 0,
    zIndex: isMenuOpen ? 10000 : 2,
    width: '100vw',
    height: '100vh',
    willChange: 'transform',
    transform: 'translateZ(0)'
  }), [isMenuOpen]);

  // Early return for desktop
  if (!isMobileOrTablet) {
    return <div className="page-content">{children}</div>;
  }

  return (
    <motion.div
      className="page-content"
      initial="closed"
      animate={isMenuOpen ? 'open' : 'closed'}
      variants={variants}
      transition={transition}
      style={motionStyles}
    >
      {children}
    </motion.div>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(FloatingPageCard); 